import React, { useState } from "react";
import { Modal, Upload, Button, message, Tooltip } from "antd";
import { UploadOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import invoiceActions from "../../../redux/invoices/actions";
import "./index.css";

const { uploadInvoice } = invoiceActions;

const humanFileSize = (size) => {
  if (!size && size !== 0) return "";
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  return `${(size / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
};

const InvoicesUploadModal = ({ visible, onClose, startDate, endDate }) => {
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);

  const beforeUpload = (file) => {
    const isAllowed = [
      "image/jpeg",
      "image/png",
      "image/heic",
      "application/pdf",
    ].includes(file.type);
    if (!isAllowed) {
      message.error("Позволени са само jpeg, png, heic, pdf!");
      return Upload.LIST_IGNORE || false;
    }

    const exists = files.some(
      (f) => f.name === file.name && f.size === file.size
    );
    if (exists) {
      message.warning(`${file.name} вече е добавен.`);
      return Upload.LIST_IGNORE || false;
    }

    const uid = `${Date.now()}-${Math.random()}`;
    const preview = URL.createObjectURL(file);
    setFiles((prev) => [
      ...prev,
      { uid, file, name: file.name, type: file.type, size: file.size, preview },
    ]);

    return false; // prevent auto upload
  };

  const removeFile = (uid) => {
    const toRemove = files.find((f) => f.uid === uid);
    if (toRemove) URL.revokeObjectURL(toRemove.preview);
    setFiles((prev) => prev.filter((f) => f.uid !== uid));
  };

  const previewFile = (uid) => {
    const f = files.find((i) => i.uid === uid);
    if (!f) return;
    window.open(f.preview, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = () => {
    if (files.length === 0) {
      message.warning("Няма избрани файлове за качване.");
      return;
    }

    const formData = new FormData();
    // append using the 'files' key so ASP.NET Core can bind to List<IFormFile> files
    files.forEach((f) => formData.append("files", f.file));

    // Upload sends files; include the current date range so saga can re-fetch with the same dates
    dispatch(uploadInvoice({ fileData: formData, startDate, endDate }));
    message.success("Файловете са изпратени за качване.");

    // cleanup and close
    files.forEach((f) => URL.revokeObjectURL(f.preview));
    setFiles([]);
    onClose();
  };

  return (
    <Modal
      className="invoices-upload-modal"
      open={visible}
      onCancel={() => {
        files.forEach((f) => URL.revokeObjectURL(f.preview));
        setFiles([]);
        onClose();
      }}
      footer={null}
      centered
      closable={false}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Upload
          multiple
          accept=".jpeg,.jpg,.png,.heic,.pdf"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={() => {}}
        >
          <Button size="small" icon={<UploadOutlined />}>
            {files.length === 0 ? "Качи фактура" : "Добави още една снимка"}
          </Button>
        </Upload>
      </div>

      <div className="invoices-selected-files" style={{ marginTop: 8 }}>
        {files.length === 0 ? (
          <div>Няма избрани файлове</div>
        ) : (
          files.map((f) => (
            <div
              key={f.uid}
              className="invoices-selected-file-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div style={{ width: 48, height: 48, flex: "0 0 48px" }}>
                {f.type && f.type.startsWith("image/") ? (
                  <img
                    src={f.preview}
                    alt={f.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                      borderRadius: 4,
                    }}
                  >
                    <span style={{ fontSize: 12 }}>PDF</span>
                  </div>
                )}
              </div>
              <div
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {f.name}
              </div>
              <div style={{ color: "#888", fontSize: 12 }}>
                {humanFileSize(f.size)}
              </div>
              <Tooltip title="Преглед">
                <Button
                  size="small"
                  onClick={() => previewFile(f.uid)}
                  icon={<EyeOutlined />}
                />
              </Tooltip>
              <Tooltip title="Премахни">
                <Button
                  size="small"
                  danger
                  onClick={() => removeFile(f.uid)}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </div>
          ))
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        <Button
          onClick={() => {
            files.forEach((f) => URL.revokeObjectURL(f.preview));
            setFiles([]);
            onClose();
          }}
        >
          Затвори
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={files.length === 0}
        >
          Добави фактура
        </Button>
      </div>
    </Modal>
  );
};

export default InvoicesUploadModal;
